import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Button, Stack } from '@mui/material';
import { defineMessage, FormattedMessage } from 'react-intl';
import { ViewErrorKind } from '../../utils/async-view/view-error-kind';

const backMessage = defineMessage({
  id: 'common.viewError.actions.back',
  defaultMessage: 'Volver',
});

const homeMessage = defineMessage({
  id: 'common.viewError.actions.home',
  defaultMessage: 'Ir al inicio',
});

const retryMessage = defineMessage({
  id: 'common.viewError.actions.retry',
  defaultMessage: 'Reintentar',
});

interface ViewErrorActionsProps {
  kind: ViewErrorKind;
  /** Retries the failed request in place. Falls back to a full page reload when omitted. */
  onRetry?: () => void;
}

/**
 * Recovery actions for a failed view. Missing/forbidden resources offer navigation
 * (going back or home), while transient failures offer a retry.
 * Navigation uses the History API and a plain anchor so this stays router-agnostic
 * (the component is shared between the router-based app and the Next.js one).
 */
export function ViewErrorActions({ kind, onRetry }: ViewErrorActionsProps) {
  const isRecoverable = kind === 'error' || kind === 'unavailable';
  const canGoBack = typeof window !== 'undefined' && window.history.length > 1;

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
      {isRecoverable ? (
        <Button
          variant="contained"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={() => (onRetry ? onRetry() : window.location.reload())}
        >
          <FormattedMessage {...retryMessage} />
        </Button>
      ) : (
        canGoBack && (
          <Button
            variant="contained"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => window.history.back()}
          >
            <FormattedMessage {...backMessage} />
          </Button>
        )
      )}

      <Button
        component="a"
        href="/"
        variant="outlined"
        color="inherit"
        size="small"
        startIcon={<HomeOutlinedIcon />}
      >
        <FormattedMessage {...homeMessage} />
      </Button>
    </Stack>
  );
}
